import React, { useState, useEffect } from 'react';
import { X, Download, ExternalLink, FileText, Image as ImageIcon } from 'lucide-react';
import { vehicleExpenseService } from '../../services/vehicleExpense.service';
import { Button } from './Button';

interface AttachmentViewerProps {
  expenseId: number;
  fileName: string;
  contentType: string;
  isOpen: boolean;
  onClose: () => void;
  onDownload?: () => void;
}

export const AttachmentViewer: React.FC<AttachmentViewerProps> = ({
  expenseId,
  fileName,
  contentType,
  isOpen,
  onClose,
  onDownload,
}) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isImage = contentType?.startsWith('image/');
  const isPdf = contentType === 'application/pdf';

  useEffect(() => {
    if (isOpen && expenseId) {
      setIsLoading(true);
      setError(null);
      
      vehicleExpenseService.downloadAttachment(expenseId)
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          setBlobUrl(url);
          setIsLoading(false);
        })
        .catch((err) => {
          console.error('Failed to load attachment:', err);
          setError('Failed to load attachment');
          setIsLoading(false);
        });
    }

    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [isOpen, expenseId]);

  if (!isOpen) return null;

  const handleDownload = async () => {
    if (onDownload) {
      onDownload();
    } else if (blobUrl) {
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-75"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-5xl max-h-[90vh] w-full mx-4 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            {isImage ? (
              <ImageIcon size={20} className="text-blue-500" />
            ) : (
              <FileText size={20} className="text-red-500" />
            )}
            <h3 className="font-medium text-gray-900 truncate max-w-md">{fileName}</h3>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={handleDownload}>
              <Download size={16} className="mr-1" />
              Download
            </Button>
            {blobUrl && (
              <a
                href={blobUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                <ExternalLink size={16} className="mr-1" />
                Open in New Tab
              </a>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full"
            >
              <X size={20} />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-auto p-4 bg-gray-100 min-h-[400px]">
          {isLoading && (
            <div className="flex items-center justify-center h-full">
              <div className="text-gray-500">Loading attachment...</div>
            </div>
          )}
          
          {error && (
            <div className="flex items-center justify-center h-full">
              <div className="text-red-500">{error}</div>
            </div>
          )}
          
          {!isLoading && !error && blobUrl && (
            <>
              {isImage && (
                <div className="flex items-center justify-center h-full">
                  <img
                    src={blobUrl}
                    alt={fileName}
                    className="max-w-full max-h-[70vh] object-contain rounded shadow-lg"
                  />
                </div>
              )}
              
              {isPdf && (
                <iframe
                  src={blobUrl}
                  title={fileName}
                  className="w-full h-[70vh] rounded shadow-lg"
                />
              )}
              
              {!isImage && !isPdf && (
                <div className="flex flex-col items-center justify-center h-full gap-4">
                  <FileText size={64} className="text-gray-400" />
                  <p className="text-gray-600">Preview not available for this file type</p>
                  <Button onClick={handleDownload}>
                    <Download size={16} className="mr-2" />
                    Download File
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
